import {Component, inject, OnInit, Renderer2, signal, ViewChild} from '@angular/core';
import {APIService, DataService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {TableComponent, TableOptions} from '@shared/components';
import {Allergy} from '@core/classes/tables/allergy';
import {HttpHeaders} from '@angular/common/http';
import {Medication} from '@core/classes/tables/medication';
import {TranslateService, Translation} from '@ngx-translate/core';
import {NavigationEnd, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {
  SurgicalProcedureStatusActionComponent
} from '@modules/cy/patient/ps/components/surgical-procedures/actions/surgical-procedure-status-action/surgical-procedure-status-action.component';
import {
  AddSurgicalProcedureComponent
} from '@modules/cy/patient/ps/components/surgical-procedures/actions/add-surgical-procedure/add-surgical-procedure.component';
import {
  SurgicalProcedureUnknownValuesComponent
} from '@modules/cy/patient/ps/components/surgical-procedures/actions/surgical-procedure-unknown-values/surgical-procedure-unknown-values.component';
import {
  MedicationUnknownValuesComponent
} from '@modules/cy/patient/ps/components/medication/actions/medication-unknown-values/medication-unknown-values.component';
import {
  AddMedicationComponent
} from '@modules/cy/patient/ps/components/medication/actions/add-medication/add-medication.component';
import {
  MedicationStatusActionComponent
} from '@modules/cy/patient/ps/components/medication/actions/medication-status-action/medication-status-action.component';
import {Subject, takeUntil} from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'ncp-medication',
  imports: [
    TableComponent
  ],
  templateUrl: './medication.component.html',
  styleUrl: './medication.component.scss',
})
export class MedicationComponent implements OnInit {
  @ViewChild('medication') public medicationEl!: TableComponent<Medication>;
  protected readonly router = inject(Router);
  protected apiService = inject(APIService);
  protected renderer2 = inject(Renderer2);
  private readonly translate = inject(TranslateService);
  protected translations: Translation = this.translate.instant('common');
  protected patientService = inject(PatientService);

  private subscription: any;
  private destroy$ = new Subject<void>();

  protected options = signal<TableOptions<Medication>>(
    new TableOptions<Medication>({
      id: 'medication',
      type: 'Medication',
      interface: Medication,
      autoLoadData: false,
      ignoreColumns: ['status'],
      pagination: {
        itemsPerPage: 10,
        pageNumber: 1,
        totalItems: 0,
      },
      searchComponent: null,
    })
  );

  ngOnInit(): void {
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('common');
    });

    this.getData(
      (this.router.url === '/cy/patient/ps/edit' || this.router.url === '/cy/patient/ps/create') ? -1 : 0,
      (this.router.url === '/cy/patient/ps/edit' || this.router.url === '/cy/patient/ps/create') ? -1 : 0
    );

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.subscription) {
          this.subscription.unsubscribe();
        }
      }
    });
  }

  protected getData = (mode: number = 0, status: number = 0) => {
    this.subscription = this.apiService
      .call({
        path: '/get-medication',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          mode: mode,
          status: status,
          version_id: this.patientService.patient?.version?.id || -1
        }
      })?.pipe(takeUntil(this.destroy$)).subscribe((response: any) => {
      if(response && response.status === 1) {
        this.options.set(
          new TableOptions<Medication>({
            id: 'medication',
            type: 'Medication',
            interface: Medication,
            data: response.data,
            autoLoadData: false,
            ignoreColumns: (this.router.url === '/cy/patient/ps/create' || this.router.url === '/cy/patient/ps/edit') ? [] : ['status'],
            actionsComponent: (this.router.url === '/cy/patient/ps/create' || this.router.url === '/cy/patient/ps/edit') ? MedicationStatusActionComponent : undefined,
            extraComponent: (this.router.url === '/cy/patient/ps/create' || this.router.url === '/cy/patient/ps/edit') ? AddMedicationComponent : undefined,
            extraLeftComponent: MedicationUnknownValuesComponent,
            pagination: {
              itemsPerPage: 10,
              pageNumber: 1,
              totalItems: response.data.length,
            },
            otherData: response.unknown,
            renderCustomRow: (td: HTMLTableCellElement, row: {}, item: string) => {
              const data = row as Medication;

              if (item === 'med_atc_label') {
                td.innerHTML = data.brand_name + ' ' + data.active_ingredient + '(' + data.atc + ')';
              } else if (item === 'intake') {
                td.innerHTML = data.number_of_intake + ' ' + data.units_per_intake;
              } else if (item === 'frequency') {
                td.innerHTML = this.translations['every'] + ' ' + data.frequency_of_intake + ' ' + this.translations[data.frequency_of_intake_unit_description + (Number(data.frequency_of_intake) > 1 ? '_multi' : '')];
              } else if (item === 'duration') {
                td.innerHTML = data.duration + ' ' + this.translations[data.duration_unit + (Number(data.duration) > 1 ? '_multi' : '')];
              } else               if (item === 'status') {
                td.innerHTML = '';

                let iEl = this.renderer2.createElement('h2');
                if(data.status === 1) {
                  this.renderer2.addClass(iEl, 'bi');
                  this.renderer2.addClass(iEl, 'bi-eye');
                  this.renderer2.addClass(iEl, 'text-primary');
                } else {
                  this.renderer2.addClass(iEl, 'bi');
                  this.renderer2.addClass(iEl, 'bi-eye-slash');
                  this.renderer2.addClass(iEl, 'text-secondary');
                }
                this.renderer2.appendChild(td, iEl);
              }
            },
            searchComponent: null,
          })
        );
      }
    })
  }
}
